/**
 * Textarea Component
 *
 * A reusable textarea component that follows the StartupHive design system.
 *
 * Props:
 * - label: string - label text
 * - id: string - textarea id (required for accessibility)
 * - name: string - textarea name
 * - value: string - controlled textarea value
 * - onChange: function - change handler
 * - placeholder: string
 * - required: boolean
 * - disabled: boolean
 * - rows: number - number of rows (default: 4)
 * - maxLength: number - character limit
 * - showCount: boolean - show character count (default: false)
 * - error: string - error message to display
 * - helperText: string - helper text below textarea
 * - className: additional CSS classes for wrapper
 * - ...rest: any other textarea attributes
 */

function Textarea({
  label,
  id,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  rows = 4,
  maxLength,
  showCount = false,
  error,
  helperText,
  className = '',
  ...rest
}) {
  const hasError = !!error
  const charCount = value?.length || 0

  return (
    <div className={`form-group ${className}`.trim()}>
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="text-error"> *</span>}
        </label>
      )}
      <textarea
        id={id}
        name={name || id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        className={`textarea ${hasError ? 'input-error' : ''}`}
        aria-invalid={hasError}
        aria-describedby={error ? `${id}-error` : helperText ? `${id}-helper` : undefined}
        {...rest}
      />
      <div className="form-footer">
        {showCount && maxLength && (
          <span className="form-count text-muted">
            {charCount} / {maxLength}
          </span>
        )}
        {error && (
          <p id={`${id}-error`} className="form-error">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${id}-helper`} className="form-helper">
            {helperText}
          </p>
        )}
      </div>
    </div>
  )
}

export default Textarea
